'use client';

import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  AlertTriangle,
  BellRing,
  Clock,
  FolderOpen,
  Gavel,
  ScrollText,
} from 'lucide-react';
import type { Sesion } from '../components/Login';
import { labelRol } from '@/lib/roles';
import { labelArea } from '@/lib/usuarios';
import { iniciales } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import StatCard, { type TonoStat } from '@/components/StatCard';
import MapaProceso from '@/components/MapaProceso';
import { kpis, ROLES_REPORTES_UI, type Kpis } from '@/lib/reportes';
import { listarExpedientes } from '@/lib/expedientes';
import { bandejaDecisiones } from '@/lib/decisionFlujo';
import { listarSolicitudes } from '@/lib/preRadicacion';
import { listarAvisos } from '@/lib/avisos';
import { Card, CardContent } from '@/components/ui/card';

// Roles que pueden listar expedientes propios (`GET /expedientes?participanteId=`).
const ROLES_MIS_EXPEDIENTES = [
  'ADMINISTRADOR', 'DIRECTOR_INSTRUCCION', 'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION', 'TECNOLOGO_APOYO', 'ABOGADO_ESPECIALISTA',
  'SUPERVISOR', 'DIRECTOR_JUZGAMIENTO',
];
// Roles que pueden leer la bandeja de decisión inicial.
const ROLES_BANDEJA_DECISION = [
  'ADMINISTRADOR', 'ABOGADO_ESPECIALISTA', 'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO', 'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION', 'TECNOLOGO_APOYO', 'DIRECTOR_INSTRUCCION',
  'SUPERVISOR',
];
// Roles que gestionan la pre-radicación (Etapa 1).
const ROLES_PRERADICACION = [
  'ADMINISTRADOR', 'TECNICO_ADMINISTRATIVO', 'APOYO_TECNICO_ADMINISTRATIVO',
];

// Saludo según la hora local (la app corre solo en el cliente tras el login).
function saludoDelDia(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 18) return 'Buenas tardes';
  return 'Buenas noches';
}

// Fecha completa en español, con la primera letra en mayúscula.
function fechaLarga(): string {
  const f = new Date().toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return f.charAt(0).toUpperCase() + f.slice(1);
}

// Panel de inicio: distinto según el rol (mismos permisos que el backend), para
// que nadie vea tarjetas de datos a los que no tiene acceso.
//  - Director/Administrador → KPIs globales reales (módulo de Reportes).
//  - Resto de roles         → "mi trabajo" (lo que tiene pendiente hoy).
// Reutiliza StatCard, los íconos del Sidebar y las etiquetas del menú para que
// se sienta parte del mismo sitio al moverse entre módulos.
export default function Dashboard({
  usuario,
  onNavegar,
}: {
  usuario: Sesion;
  onNavegar: (v: string) => void;
}) {
  const esEjecutivo = ROLES_REPORTES_UI.includes(usuario.rol);

  return (
    <div className="space-y-6">
      {/* Hero de bienvenida: gradiente verde institucional (coherente con el login),
          saludo según la hora, fecha completa y chips de rol/área. */}
      <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary via-green-700 to-green-950 p-6 text-primary-foreground shadow-sm">
        <div className="pointer-events-none absolute -right-12 -top-20 size-56 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-24 right-32 size-64 rounded-full bg-emerald-300/10 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <Avatar className="size-14 shrink-0 border-2 border-white/30">
            <AvatarFallback className="bg-white/15 text-lg font-semibold text-white">
              {iniciales(usuario.nombre)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm text-primary-foreground/75">{fechaLarga()}</p>
            <h1 className="truncate text-2xl font-semibold tracking-tight">
              {saludoDelDia()}, {usuario.nombre}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium">
                {labelRol(usuario.rol)}
              </span>
              {usuario.area && (
                <span className="inline-flex items-center rounded-full border border-white/25 px-2.5 py-0.5 text-xs font-medium text-primary-foreground/85">
                  Área: {labelArea(usuario.area)}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {esEjecutivo ? (
        <PanelEjecutivo onNavegar={onNavegar} />
      ) : (
        <PanelOperativo usuario={usuario} onNavegar={onNavegar} />
      )}

      <MapaProceso rol={usuario.rol} onNavegar={onNavegar} />
    </div>
  );
}

// ---- Vista ejecutiva: KPIs globales reales (GET /reportes/kpis) -----------

function PanelEjecutivo({ onNavegar }: { onNavegar: (v: string) => void }) {
  const [datos, setDatos] = useState<Kpis | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let activo = true;
    kpis()
      .then((d) => {
        if (activo) setDatos(d);
      })
      .catch(() => {
        if (activo) setError(true);
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => {
      activo = false;
    };
  }, []);

  const ir = () => onNavegar('reportes');

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            No se pudieron cargar los indicadores globales. Puedes consultarlos
            en{' '}
            <button
              type="button"
              onClick={ir}
              className="font-medium text-primary hover:underline"
            >
              Reportes gerenciales
            </button>
            .
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatCard
        label="Expedientes totales"
        value={datos?.totalExpedientes ?? 0}
        icon={FolderOpen}
        tone="primary"
        loading={cargando}
        onClick={ir}
      />
      <StatCard
        label="Con pliego de cargos"
        value={`${datos?.porcentajeConPliego ?? 0}%`}
        icon={ScrollText}
        tone="primary"
        loading={cargando}
        onClick={ir}
      />
      <StatCard
        label="Próximos a prescribir"
        value={datos?.proximosAPrescribir ?? 0}
        icon={Clock}
        tone="amber"
        loading={cargando}
        onClick={ir}
      />
      <StatCard
        label="Términos vencidos"
        value={datos?.terminosVencidos ?? 0}
        icon={AlertTriangle}
        tone="red"
        loading={cargando}
        onClick={ir}
      />
      <StatCard
        label="Archivados"
        value={`${datos?.porcentajeArchivos ?? 0}%`}
        icon={Archive}
        tone="slate"
        loading={cargando}
        onClick={ir}
      />
    </div>
  );
}

// ---- Vista operativa: "mi trabajo" (solo lo que el rol puede consultar) ---

interface TareaCard {
  key: string;
  label: string;
  icon: LucideIcon;
  tone: TonoStat;
  vista: string;
  valor: number | null;
  cargando: boolean;
}

// Qué tarjetas le corresponden a cada rol — refleja exactamente los mismos
// grupos de @Roles del backend (expedientes/decision-flujo/pre-radicacion),
// para no disparar peticiones que terminarían en 403.
function plantillaTareas(rol: string): TareaCard[] {
  const t: TareaCard[] = [];
  if (ROLES_PRERADICACION.includes(rol)) {
    t.push({
      key: 'preradicacion', label: 'Solicitudes por radicar', icon: Clock,
      tone: 'amber', vista: 'actas-ver', valor: null, cargando: true,
    });
  }
  if (ROLES_MIS_EXPEDIENTES.includes(rol)) {
    t.push({
      key: 'expedientes', label: 'Mis expedientes', icon: FolderOpen,
      tone: 'primary', vista: 'mis-expedientes', valor: null, cargando: true,
    });
  }
  if (ROLES_BANDEJA_DECISION.includes(rol)) {
    t.push({
      key: 'decision',
      label: rol === 'TECNICO_ADMINISTRATIVO' || rol === 'APOYO_TECNICO_ADMINISTRATIVO'
        ? 'Expedientes devueltos' : 'Pendientes de decisión',
      icon: Gavel, tone: 'primary', vista: 'decision-inicial', valor: null, cargando: true,
    });
  }
  return t;
}

async function cargarTarea(key: string, usuario: Sesion): Promise<number> {
  if (key === 'preradicacion') {
    const s = await listarSolicitudes('EN_PROCESO');
    return s.length;
  }
  if (key === 'expedientes') {
    const p = await listarExpedientes({ participanteId: usuario.id, limit: 1 });
    return p.meta.total;
  }
  if (key === 'decision') {
    const b = await bandejaDecisiones();
    return b.length;
  }
  return 0;
}

function PanelOperativo({
  usuario,
  onNavegar,
}: {
  usuario: Sesion;
  onNavegar: (v: string) => void;
}) {
  const [tareas, setTareas] = useState<TareaCard[]>(() => plantillaTareas(usuario.rol));
  const [avisos, setAvisos] = useState<{ valor: number | null; cargando: boolean }>({
    valor: null,
    cargando: true,
  });

  useEffect(() => {
    const plantilla = plantillaTareas(usuario.rol);
    setTareas(plantilla);

    plantilla.forEach((t) => {
      cargarTarea(t.key, usuario)
        .then((valor) => {
          setTareas((prev) => prev.map((x) => (x.key === t.key ? { ...x, valor, cargando: false } : x)));
        })
        .catch(() => {
          setTareas((prev) => prev.map((x) => (x.key === t.key ? { ...x, valor: null, cargando: false } : x)));
        });
    });

    setAvisos({ valor: null, cargando: true });
    listarAvisos()
      .then((r) => setAvisos({ valor: r.noLeidos, cargando: false }))
      .catch(() => setAvisos({ valor: null, cargando: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.rol]);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {tareas.map((t) => (
        <StatCard
          key={t.key}
          label={t.label}
          value={t.valor ?? '—'}
          icon={t.icon}
          tone={t.tone}
          loading={t.cargando}
          onClick={() => onNavegar(t.vista)}
        />
      ))}
      <StatCard
        label="Avisos sin leer"
        value={avisos.valor ?? '—'}
        icon={BellRing}
        tone={avisos.valor ? 'amber' : 'slate'}
        loading={avisos.cargando}
      />
    </div>
  );
}
